import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { webNavigate } from "../../services/navigate.service";
import BackIcon from "../../images/BackIcon";
import LoadingV2 from "../../components/LoadingV2";
import { DropdownSearch } from "../../components/DropdownSearch";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { useResponsive } from "../../hook/useResponsive";
import { getLocations } from "../../services/locations.services";
import {
  Field,
  FieldLabel,
  StickyFooter,
  STICKY_FOOTER_HEIGHT,
  TAP,
  TEAL,
  TEXT,
} from "../../components/mobile";
import {
  DateTimeField,
  FormGrid,
  FormGridFull,
  PickerField,
  Stepper,
  tableLoadHint,
  useLocationTables,
} from "./reservationShared";
import {
  createTableReservation,
  CreateTableReservationPayload,
} from "../../services/tableReservations.service";

/**
 * Back-office create flow for `dbo.table_reservations`.
 *
 * Visual language matches `TableReservationsDetail.tsx` — `font-inter`,
 * brand teal `#0F7679`, rounded `30px` primary buttons,
 * `1px solid #C9D7D8` secondary buttons, `LoadingV2` overlay,
 * `BackIcon` for the back link, and the same `FormField` two-column layout
 * (single column on mobile via `useResponsive`).
 *
 * Server-controlled fields:
 * - `source` is fixed server-side to `ReservationSource.Dashboard` (FE never
 *   sends it).
 * - `status` defaults to Confirmed on the BE.
 * - `reservedTo` is derived from `reserved_from` + the location's
 *   `default_duration_minutes`.
 * - `reservationNumber` is generated server-side.
 *
 * Validation rules surfaced as 400s (paused, blocked period, capacity over
 * the slot cap, party size over `max_party_size`, outside operating hours,
 * outside booking window) are passed through to the toast verbatim so users
 * see the BE's exact `message` rather than a generic "failed to save" line.
 */

interface LocationOption {
  id: string;
  name: string;
  locationCode?: string;
}

const t = (language: any, key: string, fallback?: string) =>
  (language && language[key]) || fallback || key;

const buttonPrimaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "#0F7679",
  padding: "10px 24px",
  border: "none",
  cursor: "pointer",
};

const buttonSecondaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  padding: "10px 24px",
  border: "1px solid #C9D7D8",
  color: "#222525",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "transparent",
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  height: "48px",
  width: "100%",
  border: "1px solid #EBEBED",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px",
  outline: "none",
  background: "transparent",
  fontFamily: "Inter, sans-serif",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: "120px",
  resize: "vertical",
};

const labelStyle: React.CSSProperties = {
  color: "#4A5353",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  fontFamily: "Inter, sans-serif",
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: "20px",
  fontWeight: 500,
  color: "#222525",
  lineHeight: "28px",
};

interface FormState {
  locationId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  partySize: string;
  reservedFrom: Date | null;
  floorPlanDetailId: string;
  notes: string;
}

const initialForm: FormState = {
  locationId: "",
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  partySize: "",
  reservedFrom: null,
  floorPlanDetailId: "",
  notes: "",
};

const TableReservationsCreate: React.FC = () => {
  const { isMobile, isTablet, isTouchLayout } = useResponsive();
  const preLanguage = localStorage.getItem("language") || "";
  const [language, setLanguage] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [form, setForm] = useState<FormState>(initialForm);

  const [locationLabel, setLocationLabel] = useState("");
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  const [tableLabel, setTableLabel] = useState("");
  const tableDropdownRef = useRef<HTMLDivElement>(null);
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const res: any = await getLocations(undefined, undefined, 1, 200, "");
      const rows: LocationOption[] =
        res?.data?.data?.map((l: any) => ({
          id: l.id,
          locationCode: l.locationCode,
          name: l.name,
        })) ?? [];
      setLocations(rows);
    } catch {
      // Silent — the location dropdown will simply be empty; the user can
      // back out and retry. We don't toast here because the page is already
      // on screen with no actionable error context.
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  // Per-field validation for the touch layout: the same rules `validate()`
  // below enforces for the desktop toast, keyed by field so the sticky-footer
  // Save can mark everything touched, render the messages inline and scroll to
  // the first one instead of firing a single toast and leaving the user to
  // hunt for the field.
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const fieldErrors: Record<string, string | undefined> = useMemo(() => {
    const errors: Record<string, string | undefined> = {};
    if (!form.locationId) errors.locationId = t(language, "Location is required");
    if (!form.customerName.trim())
      errors.customerName = t(language, "Customer name is required");
    if (!form.reservedFrom) {
      errors.reservedFrom = t(
        language,
        "Reservation date and time is required"
      );
    } else if (form.reservedFrom.getTime() < Date.now()) {
      errors.reservedFrom = t(
        language,
        "Reservation date and time must be in the future"
      );
    }
    const parsedPartySize = parseInt(form.partySize, 10);
    if (
      !Number.isFinite(parsedPartySize) ||
      parsedPartySize < 1 ||
      parsedPartySize > 500
    ) {
      errors.partySize = t(language, "Party size must be between 1 and 500");
    }
    if (form.customerEmail.trim()) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(form.customerEmail.trim())) {
        errors.customerEmail = t(language, "Customer email is not valid");
      }
    }
    return errors;
  }, [form, language]);

  /** The inline message for a field, once the user has been given a reason to see it. */
  const errorFor = (field: string) =>
    submitted || touched[field] ? fieldErrors[field] : undefined;

  /** Order the touch form lays the fields out in — drives "scroll to the first error". */
  const FIELD_ORDER = [
    "locationId",
    "reservedFrom",
    "customerName",
    "partySize",
    "customerEmail",
  ];

  const { tables, loading: tablesLoading, error: tablesError } =
    useLocationTables(form.locationId, language);

  const handleBack = () => {
    webNavigate(`/${preLanguage}/tableReservations`);
  };

  const validate = (): string | null => {
    if (!form.locationId) return t(language, "Location is required");
    if (!form.customerName.trim())
      return t(language, "Customer name is required");
    if (!form.reservedFrom)
      return t(language, "Reservation date and time is required");

    const parsedPartySize = parseInt(form.partySize, 10);
    if (
      !Number.isFinite(parsedPartySize) ||
      parsedPartySize < 1 ||
      parsedPartySize > 500
    ) {
      return t(language, "Party size must be between 1 and 500");
    }

    if (form.reservedFrom.getTime() < Date.now()) {
      return t(language, "Reservation date and time must be in the future");
    }

    // The BE re-validates email format; this is a courtesy preflight so the
    // user sees it before the network round-trip.
    if (form.customerEmail.trim()) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(form.customerEmail.trim())) {
        return t(language, "Customer email is not valid");
      }
    }

    return null;
  };

  const handleSave = async () => {
    if (isTouchLayout) {
      // Never a silent no-op: mark everything touched so the inline messages
      // appear, then scroll to the first field that has one.
      setSubmitted(true);
      const firstBad = FIELD_ORDER.find((field) => fieldErrors[field]);
      if (firstBad) {
        document
          .getElementById(`reservation-field-${firstBad}`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }
    } else {
      const errMsg = validate();
      if (errMsg) {
        showToast({ message: errMsg, type: "warning" });
        return;
      }
    }

    const payload: CreateTableReservationPayload = {
      locationId: form.locationId,
      partySize: parseInt(form.partySize, 10),
      reservedFrom: (form.reservedFrom as Date).toISOString(),
      customerName: form.customerName,
      customerPhone: form.customerPhone || null,
      customerEmail: form.customerEmail || null,
      floorPlanDetailId: form.floorPlanDetailId.trim() || null,
      notes: form.notes || null,
    };

    setSaving(true);
    try {
      const created = await createTableReservation(payload);
      showToast({
        message: t(language, "Reservation created"),
        type: "success",
      });
      // Navigate to the detail page so the user can immediately verify and
      // continue editing (assign a table, change status, etc.).
      webNavigate(`/${preLanguage}/tableReservations/${created.id}`);
    } catch (err: any) {
      // BE returns 400 with a human-readable `message` for paused / blocked /
      // capacity / window / hours violations; surface it verbatim. The
      // generic fallback covers 5xx + transport errors.
      showApiError(err, { language, titleFallback: t(language, "Failed to create reservation") });
    } finally {
      setSaving(false);
    }
  };

  const tableHint = !form.locationId
    ? t(language, "Pick a location first")
    : tableLoadHint(language, tablesError, tables.length === 0 && !tablesLoading) ??
      t(language, "Optional — the table can be assigned later");

  if (isTouchLayout) {
    /**
     * Touch layout (<1024 — the host shell shows its sidebar from 1024 up,
     * so viewport-fixed chrome stops just below it). One scroll surface,
     * labels above 48px/16px
     * fields, every choice a sheet or a stepper, the primary action pinned in
     * a sticky footer. Tablet (700–1023px, the front-of-house iPad) lays the
     * fields out two-up: this is a dense field grid, so a second column halves
     * the scroll instead of leaving the iPad's right half empty. Notes span
     * both columns.
     */
    return (
      <div className="flex flex-col h-full min-h-0" style={{ position: "relative" }}>
        {(saving || loadingLocations) && <LoadingV2 />}
        <div
          className="scroll-container"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: `${STICKY_FOOTER_HEIGHT}px`,
          }}
        >
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center font-inter"
            style={{
              gap: "8px",
              height: `${TAP}px`,
              padding: 0,
              border: "none",
              background: "transparent",
              color: TEAL,
              fontSize: "15px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <BackIcon />
            {t(language, "Back")}
          </button>

          <p
            className="font-semibold font-inter"
            style={{ fontSize: "22px", lineHeight: "28px", color: TEXT, margin: "4px 0 16px" }}
          >
            {t(language, "Create reservation")}
          </p>

          <FormGrid isTablet={isTablet}>
            <div id="reservation-field-locationId">
              <PickerField
                language={language}
                label={t(language, "Location")}
                placeholder={t(language, "Select location")}
                options={locations.map((loc) => ({ id: loc.id, label: loc.name }))}
                value={form.locationId}
                required
                error={errorFor("locationId")}
                onChange={(id) => {
                  markTouched("locationId");
                  // A table belongs to one location's floor plan — changing
                  // the location invalidates whatever was picked.
                  setForm((prev) => ({
                    ...prev,
                    locationId: id,
                    floorPlanDetailId: "",
                  }));
                  setLocationLabel(locations.find((l) => l.id === id)?.name ?? "");
                }}
              />
            </div>

            <div id="reservation-field-reservedFrom">
              <DateTimeField
                language={language}
                label={t(language, "Reserved from")}
                value={form.reservedFrom}
                required
                futureOnly
                error={errorFor("reservedFrom")}
                onChange={(date) => {
                  markTouched("reservedFrom");
                  setForm((prev) => ({ ...prev, reservedFrom: date }));
                }}
              />
            </div>

            <div id="reservation-field-customerName">
              <Field
                label={t(language, "Customer name")}
                required
                value={form.customerName}
                maxLength={200}
                autoComplete="name"
                error={errorFor("customerName")}
                onBlur={() => markTouched("customerName")}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, customerName: e.target.value }))
                }
              />
            </div>

            <div id="reservation-field-partySize">
              <Stepper
                language={language}
                label={t(language, "Party size")}
                value={form.partySize}
                required
                error={errorFor("partySize")}
                onChange={(value) => {
                  markTouched("partySize");
                  setForm((prev) => ({ ...prev, partySize: value }));
                }}
              />
            </div>

            <div id="reservation-field-customerPhone">
              <Field
                label={t(language, "Customer phone")}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t(language, "Optional")}
                maxLength={50}
                value={form.customerPhone}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, customerPhone: e.target.value }))
                }
              />
            </div>

            <div id="reservation-field-customerEmail">
              <Field
                label={t(language, "Customer email")}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t(language, "Optional")}
                maxLength={200}
                value={form.customerEmail}
                error={errorFor("customerEmail")}
                onBlur={() => markTouched("customerEmail")}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, customerEmail: e.target.value }))
                }
              />
            </div>

            <div id="reservation-field-floorPlanDetailId">
              <PickerField
                language={language}
                label={t(language, "Table")}
                sheetTitle={t(language, "Assign a table")}
                placeholder={t(language, "No table assigned")}
                options={tables}
                value={form.floorPlanDetailId}
                loading={tablesLoading}
                disabled={!form.locationId}
                hint={tableHint}
                clearLabel={t(language, "No table assigned")}
                onChange={(id) =>
                  setForm((prev) => ({ ...prev, floorPlanDetailId: id }))
                }
              />
            </div>

            <FormGridFull isTablet={isTablet}>
              <div style={{ marginBottom: "16px" }}>
                <FieldLabel htmlFor="reservation-notes">
                  {t(language, "Notes")}
                </FieldLabel>
                <textarea
                  id="reservation-notes"
                  value={form.notes}
                  maxLength={2000}
                  placeholder={t(language, "Internal notes")}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="font-inter placeholder:text-placeholder"
                  style={{
                    ...inputStyle,
                    height: "120px",
                    padding: "12px 14px",
                    fontSize: "16px",
                    resize: "vertical",
                    borderColor: "#C9D7D8",
                  }}
                />
              </div>
            </FormGridFull>
          </FormGrid>
        </div>

        <StickyFooter
          cancelLabel={t(language, "Cancel")}
          onCancel={handleBack}
          primaryLabel={
            saving ? t(language, "Saving…") : t(language, "Create reservation")
          }
          onPrimary={handleSave}
          primaryDisabled={saving}
        />
      </div>
    );
  }

  return (
    <div
      className="h-full relative"
      style={{ paddingBottom: "96px", overflowY: "auto" }}
    >
      {(saving || loadingLocations) && <LoadingV2 />}

      <div
        className="flex items-center cursor-pointer"
        style={{ gap: "8px", marginBottom: "16px" }}
        onClick={handleBack}
      >
        <BackIcon />
        <span
          className="font-inter"
          style={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#0F7679",
          }}
        >
          {t(language, "Back")}
        </span>
      </div>

      <p
        className="font-inter"
        style={{
          fontSize: "24px",
          fontWeight: 500,
          color: "#222525",
          marginBottom: "24px",
        }}
      >
        {t(language, "Create reservation")}
      </p>

      <div
        style={{
          border: "1px solid #EBF0F0",
          borderRadius: "12px",
          backgroundColor: "#FFFFFF",
          padding: "24px",
        }}
      >
        <p style={{ ...sectionTitleStyle, marginBottom: "20px" }}>
          {t(language, "Reservation details")}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <FormField
            label={t(language, "Location")}
            isMobile={isMobile}
            required
          >
            {/* Same 48px wrapper as the detail page so the DropdownSearch
                doesn't stretch to the FormField's flex-column auto-grow. */}
            <div style={{ height: "48px" }}>
              <DropdownSearch
                refKey="locationId"
                refs={{ locationId: locationDropdownRef }}
                openStates={{ locationId: locationDropdownOpen }}
                open={() => setLocationDropdownOpen(true)}
                close={() => setLocationDropdownOpen(false)}
                data={locations}
                labelExtractor={(item: any) => item.name}
                value={locationLabel}
                setValue={setLocationLabel}
                onSelect={(item: any) => {
                  setForm((prev) => ({
                    ...prev,
                    locationId: item.id,
                    // A table belongs to one location's floor plan.
                    floorPlanDetailId: "",
                  }));
                  setLocationLabel(item.name);
                  setTableLabel("");
                  setLocationDropdownOpen(false);
                }}
                placeholder={t(language, "Select location")}
              />
            </div>
          </FormField>

          <FormField
            label={t(language, "Reserved from")}
            isMobile={isMobile}
            required
          >
            <DatePicker
              selected={form.reservedFrom}
              onChange={(date: Date | null) =>
                setForm((prev) => ({ ...prev, reservedFrom: date }))
              }
              showTimeSelect
              timeIntervals={15}
              dateFormat="dd-MM-yyyy HH:mm"
              minDate={new Date()}
              placeholderText={t(language, "Pick a date and time")}
              className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none bg-white"
              wrapperClassName="reservations-create-datetime"
            />
          </FormField>

          <FormField
            label={t(language, "Customer name")}
            isMobile={isMobile}
            required
          >
            <input
              type="text"
              value={form.customerName}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customerName: e.target.value }))
              }
              style={inputStyle}
              maxLength={200}
            />
          </FormField>

          <FormField
            label={t(language, "Party size")}
            isMobile={isMobile}
            required
          >
            <input
              type="number"
              min={1}
              max={500}
              value={form.partySize}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, partySize: e.target.value }))
              }
              style={inputStyle}
            />
          </FormField>

          <FormField label={t(language, "Customer phone")} isMobile={isMobile}>
            <input
              type="text"
              value={form.customerPhone}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customerPhone: e.target.value }))
              }
              style={inputStyle}
              placeholder={t(language, "Optional")}
              maxLength={50}
            />
          </FormField>

          <FormField label={t(language, "Customer email")} isMobile={isMobile}>
            <input
              type="email"
              value={form.customerEmail}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customerEmail: e.target.value }))
              }
              style={inputStyle}
              placeholder={t(language, "Optional")}
              maxLength={200}
            />
          </FormField>

          {/* Chosen by name + covers from the selected location's floor plan,
              in the same DropdownSearch idiom as Location above — it replaced
              a free-text box that asked the user to paste a
              floor_plan_details UUID. */}
          <FormField
            label={t(language, "Table")}
            isMobile={isMobile}
            hint={tableHint}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ height: "48px", flex: 1, minWidth: 0 }}>
                <DropdownSearch
                  refKey="table"
                  refs={{ table: tableDropdownRef }}
                  openStates={{ table: tableDropdownOpen }}
                  open={() => setTableDropdownOpen(true)}
                  close={() => setTableDropdownOpen(false)}
                  data={tables}
                  labelExtractor={(item: any) =>
                    item.hint ? `${item.label} · ${item.hint}` : item.label
                  }
                  value={tableLabel}
                  setValue={setTableLabel}
                  onSelect={(item: any) => {
                    setForm((prev) => ({
                      ...prev,
                      floorPlanDetailId: item.id,
                    }));
                    setTableLabel(item.label);
                    setTableDropdownOpen(false);
                  }}
                  loading={tablesLoading}
                  disabled={!form.locationId}
                  placeholder={t(language, "No table assigned")}
                />
              </div>
              {form.floorPlanDetailId && (
                <button
                  type="button"
                  style={buttonSecondaryStyle}
                  onClick={() => {
                    setForm((prev) => ({ ...prev, floorPlanDetailId: "" }));
                    setTableLabel("");
                  }}
                >
                  {t(language, "Clear")}
                </button>
              )}
            </div>
          </FormField>

          <FormField label={t(language, "Notes")} isMobile={isMobile} fullWidth>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              style={textareaStyle}
              placeholder={t(language, "Internal notes")}
              maxLength={2000}
            />
          </FormField>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            marginTop: "24px",
          }}
        >
          <button
            type="button"
            style={buttonSecondaryStyle}
            onClick={handleBack}
            disabled={saving}
          >
            {t(language, "Cancel")}
          </button>
          <button
            type="button"
            style={buttonPrimaryStyle}
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? t(language, "Saving…")
              : t(language, "Create reservation")}
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField: React.FC<{
  label: string;
  children: React.ReactNode;
  isMobile?: boolean;
  fullWidth?: boolean;
  hint?: string;
  required?: boolean;
}> = ({ label, children, isMobile, fullWidth, hint, required }) => (
  <div
    style={{
      width: fullWidth || isMobile ? "100%" : "calc(50% - 12px)",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}
  >
    <label style={labelStyle}>
      {label}
      {required && (
        <span style={{ color: "#D14343", marginLeft: "4px" }} aria-hidden>
          *
        </span>
      )}
    </label>
    {children}
    {hint && (
      <span
        style={{
          ...labelStyle,
          fontSize: "12px",
          color: "#6A7878",
        }}
      >
        {hint}
      </span>
    )}
  </div>
);

export default TableReservationsCreate;
